import * as React from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch } from 'react-redux';

import once from 'lodash/once';
import isEmpty from 'lodash/isEmpty';

import { Comment, CommentsActionCreators, getActionCreators } from '@common/react/store/Comments';
import { BaseUser } from '@common/react/objects/BaseUser';
import { BaseParams } from '@common/typescript/objects/BaseParams';

export interface CommentsContext {
	initComments: <TUser extends BaseUser>(comment: Comment<TUser>, objectSubType: string, objectId: number, store?: string) => void;
	loadComments: (requestType: string, objectType: string, objectId: number, store?: string, additionalParams?: BaseParams) => void;
}

export const createCommentsContext = once(() => React.createContext({} as CommentsContext));

export const useCommentsContext: () => CommentsContext = () => {
	const context: CommentsContext = React.useContext(createCommentsContext());

	if (isEmpty(context)) throw 'Need CommentsContext context!';

	return context;
};

interface Props {
	children?: React.ReactNode | ((actions: CommentsContext) => React.ReactNode);
}

export const CommentsProvider: <TUser extends BaseUser>(
	props: Props,
) => React.ReactElement<TUser> = <TUser extends BaseUser>({
	children,
}: Props) => {
	const CommentsContext = createCommentsContext();

	const dispatch = useDispatch();
	const commentsActions: CommentsActionCreators<TUser> = bindActionCreators(getActionCreators(), dispatch);

	const actions = {
		initComments: (comment: Comment<TUser>, objectSubType: string, objectId: number, store?: string) => {
			commentsActions.setTree(comment, objectSubType, objectId, store);
		},
		loadComments: (requestType: string, objectType: string, objectId: number, store?: string, additionalParams?: BaseParams) => {
			commentsActions.loadTree(requestType, objectType, objectId, store, additionalParams);
		},
	} as CommentsContext;

	return (
		<>
			<CommentsContext.Provider value={actions}>
				{typeof children === 'function' ? children(actions) : children}
			</CommentsContext.Provider>
		</>
	);
};
